import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  User, 
  Lock, 
  Shield, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import usuariosService from '../services/usuariosService'
import LoadingSpinner from '../components/LoadingSpinner'

const UsuarioForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { hasPermission } = useAuth()
  
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  
  const [formData, setFormData] = useState({
    usuario: '',
    password: '',
    confirmPassword: '',
    permisos: []
  })
  
  const [errors, setErrors] = useState({})

  const isEditing = Boolean(id)

  useEffect(() => {
    if (isEditing) {
      cargarUsuario()
    }
  }, [id])

  const cargarUsuario = async () => {
    try {
      setLoading(true)
      const response = await usuariosService.getUsuario(id)
      const usuario = response.data
      
      setFormData({
        usuario: usuario.usuario || '',
        password: '',
        confirmPassword: '',
        permisos: usuario.permisos || []
      })
    } catch (error) {
      console.error('Error cargando usuario:', error)
      setError('Error al cargar el usuario')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const handlePermisoChange = (permiso) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.includes(permiso)
        ? prev.permisos.filter(p => p !== permiso)
        : [...prev.permisos, permiso]
    }))
  }

  const getPermisosCategorizados = () => {
    const categorias = {
      'Gestión de Usuarios': [
        'VER_USUARIOS',
        'AGREGAR_USUARIO', 
        'EDITAR_USUARIO',
        'ELIMINAR_USUARIO'
      ],
      'Gestión de Clientes': [
        'VER_CLIENTES',
        'AGREGAR_CLIENTE',
        'EDITAR_CLIENTE', 
        'ELIMINAR_CLIENTE',
        'CAMBIAR_DEUDA',
        'ENVIAR_MENSAJE_PRUEBA'
      ],
      'Empresas WhatsApp': [
        'VER_EMPRESAS',
        'AGREGAR_EMPRESA',
        'EDITAR_EMPRESA',
        'ELIMINAR_EMPRESA'
      ],
      'Simulaciones GPS': [
        'VER_SIMULACIONES_GPS',
        'AGREGAR_SIMULACION_GPS',
        'EDITAR_SIMULACION_GPS',
        'ELIMINAR_SIMULACION_GPS',
        'DUPLICAR_SIMULACION_GPS',
        'NUEVA_SIMULACION',
        'COPIAR_SIMULACION',
        'EDITAR_SIMULACION',
        'ELIMINAR_SIMULACION'
      ],
      'Réplicas GPS': [
        'VER_REPLICAS_GPS',
        'COPIAR_REPLICAS',
        'EDITAR_REPLICAS',
        'ELIMINAR_REPLICAS',
        'ACTUALIZAR_FECHAS_GPS'
      ],
      'Herramientas GPS': [
        'SIMULAR_POSICION_GPS',
        'SIMULAR_POSICION',
        'GENERAR_RUTA_GPS',
        'GENERAR_RUTA'
      ]
    }
    return categorias
  }

  // Función para obtener el total de permisos disponibles
  const getTotalPermisosDisponibles = () => {
    const categorias = getPermisosCategorizados()
    return Object.values(categorias).flat().length
  }

  const handleSelectCategoria = (categoria) => {
    const permisosCategoria = getPermisosCategorizados()[categoria]
    const todosSeleccionados = permisosCategoria.every(permiso => 
      formData.permisos.includes(permiso)
    )
    
    if (todosSeleccionados) {
      // Deseleccionar todos los permisos de la categoría
      setFormData(prev => ({
        ...prev,
        permisos: prev.permisos.filter(permiso => !permisosCategoria.includes(permiso))
      }))
    } else {
      // Seleccionar todos los permisos de la categoría
      const permisosNuevos = permisosCategoria.filter(permiso => 
        !formData.permisos.includes(permiso)
      )
      setFormData(prev => ({
        ...prev,
        permisos: [...prev.permisos, ...permisosNuevos]
      }))
    }
  }

  const getCategoriaSeleccionada = (categoria) => {
    const permisosCategoria = getPermisosCategorizados()[categoria]
    const seleccionados = permisosCategoria.filter(permiso => 
      formData.permisos.includes(permiso)
    ).length
    return seleccionados === permisosCategoria.length
  }

  const getCategoriaParcial = (categoria) => {
    const permisosCategoria = getPermisosCategorizados()[categoria]
    const seleccionados = permisosCategoria.filter(permiso => 
      formData.permisos.includes(permiso)
    ).length
    return seleccionados > 0 && seleccionados < permisosCategoria.length
  }

  // Función para seleccionar todos los permisos
  const handleSelectAllPermisos = () => {
    const todosLosPermisos = Object.values(getPermisosCategorizados()).flat()
    setFormData(prev => ({
      ...prev,
      permisos: todosLosPermisos
    }))
  }

  const handleClearAllPermisos = () => {
    setFormData(prev => ({
      ...prev,
      permisos: []
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.usuario.trim()) {
      newErrors.usuario = 'El nombre de usuario es requerido'
    } else if (formData.usuario.length < 3) {
      newErrors.usuario = 'El nombre de usuario debe tener al menos 3 caracteres'
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'La contraseña es requerida'
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres'
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const usuarioData = {
        usuario: formData.usuario.trim(),
        permisos: formData.permisos
      }

      if (formData.password) {
        usuarioData.password = formData.password
      }

      if (isEditing) {
        await usuariosService.updateUsuario(id, usuarioData)
        setSuccess('Usuario actualizado exitosamente')
      } else {
        await usuariosService.createUsuario(usuarioData)
        setSuccess('Usuario creado exitosamente')
      }

      // Redirigir después de un breve delay
      setTimeout(() => {
        navigate('/usuarios')
      }, 1500)

    } catch (error) {
      console.error('Error guardando usuario:', error)
      setError(error.response?.data?.message || 'Error al guardar el usuario')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <User className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
            </h1>
            <p className="text-gray-600">
              {isEditing ? 'Modifica la información del usuario' : 'Crea un nuevo usuario del sistema'}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/usuarios')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <X className="w-5 h-5" />
          Cancelar
        </button>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de Usuario *
              </label>
              <input
                type="text"
                name="usuario"
                value={formData.usuario}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.usuario ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingrese el nombre de usuario"
              />
              {errors.usuario && (
                <p className="mt-1 text-sm text-red-600">{errors.usuario}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña {!isEditing && '*'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={isEditing ? 'Dejar vacío para mantener la actual' : 'Ingrese la contraseña'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
          </div>

          {/* Confirmar contraseña */}
          {formData.password && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Confirme la contraseña"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          )}

          {/* Permisos */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Permisos del Usuario
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllPermisos}
                  className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                >
                  Seleccionar Todos
                </button>
                <button
                  type="button"
                  onClick={handleClearAllPermisos}
                  className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded hover:bg-gray-200"
                >
                  Limpiar Todos
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {Object.entries(getPermisosCategorizados()).map(([categoria, permisos]) => (
                <div key={categoria} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">{categoria}</h3>
                    <button
                      type="button"
                      onClick={() => handleSelectCategoria(categoria)}
                      className={`text-xs px-2 py-1 rounded ${
                        getCategoriaSeleccionada(categoria)
                          ? 'bg-green-100 text-green-800'
                          : getCategoriaParcial(categoria)
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      } hover:opacity-80`}
                    >
                      {getCategoriaSeleccionada(categoria) ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {permisos.map((permiso) => (
                      <label key={permiso} className="flex items-center gap-2 p-2 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.permisos.includes(permiso)}
                          onChange={() => handlePermisoChange(permiso)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{permiso}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {formData.permisos.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Permisos seleccionados:</strong> {formData.permisos.length} de {getTotalPermisosDisponibles()}
                </p>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/usuarios')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Actualizar Usuario' : 'Crear Usuario'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Mensajes de error y éxito */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        </div>
      )}
    </div>
  )
}

export default UsuarioForm 